<script lang="ts" setup>
import AccountPwChange from '@/components/dashboard/Account/AccountPwChange.vue'
import AccountSessions from '@/components/dashboard/Account/AccountSessions.vue'
import AccountTotp from '@/components/dashboard/Account/AccountTotp.vue'
import { onMounted, ref, watch } from 'vue'
import type { AccountDetails } from '@/types'

const props = defineProps<{
  accountDetails: AccountDetails
}>()

const accountDetailsRef = ref<AccountDetails>({} as AccountDetails)

watch(
  () => props.accountDetails,
  () => {
    accountDetailsRef.value = props.accountDetails
  }
)

onMounted(() => {
  accountDetailsRef.value = props.accountDetails
})
</script>

<template>
  <div class="security-page">
    <!-- Section Header -->
    <div class="section-header">
      <h2 class="section-title">{{ $t('dashboard.account.security.title') }}</h2>
      <p class="section-description">{{ $t('dashboard.account.security.description') }}</p>
    </div>

    <!-- Security Cards -->
    <div class="security-cards">
      <!-- 2FA Card -->
      <div class="security-card">
        <AccountTotp :has-totp="accountDetailsRef.totp" />
      </div>

      <!-- Password Card -->
      <div class="security-card">
        <AccountPwChange />
      </div>

      <!-- Sessions Card -->
      <div class="security-card">
        <AccountSessions />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.security-page {
  width: 100%;
}

/* Section Header */
.section-header {
  margin-bottom: 28px;
}

.section-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 4px;
}

.section-description {
  font-size: 0.875rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

/* Security Cards */
.security-cards {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.security-card {
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 28px;
}

/* Light Theme */
.v-theme--lightTheme {
  .section-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .section-description {
    color: rgba(15, 23, 42, 0.5);
  }

  .security-card {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.08);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .security-card {
    padding: 20px;
    border-radius: 12px;
  }
}
</style>
